/**
 * Die Scroll-Wache: sagt im Protokoll, WANN und WO eine Ansicht springt.
 * (30.09.2026, Ticket 2873)
 *
 * Martin, iPhone: *„Wenn ich hin- und herscrolle, dann springt das
 * unkontrolliert nach unten oder nach oben … überall ist das Scrollen
 * fehlerhaft."*
 *
 * Auf dem Server lässt sich das Springen nicht nachstellen (kein iPhone,
 * WebKit startet dort nicht). Also misst das Gerät selbst und schickt eine
 * Zeile `[scroll-t] …` ins Journal (derselbe Weg wie die Mikro-Messung,
 * `meldeSpur`). Drei Formen:
 *
 *   sprung    ein einziges Scroll-Ereignis bewegt die Ansicht um ≥ 220 Punkte
 *             — binnen 100 ms nach dem letzten oder bei aufliegendem Finger
 *             (ein Finger schafft das in einem Bild nicht; ein Wisch mit
 *             Schwung macht Schritte, die zur Bewegung davor passen)
 *   umkehr    zwei Richtungswechsel hintereinander, je ≥ 40 Punkte, binnen
 *             500 ms — die Ansicht pendelt
 *   hoehe     die Inhaltshöhe ändert sich um ≥ 40 Punkte, während gescrollt
 *             wird — etwas schiebt den Inhalt
 *   tastatur-ohne-feld  die Höhenrechnung sagt „Tastatur“, aber kein Feld hat den
 *             Fokus — früher hätte das die Fußzeile weggenommen (tastatur.ts)
 *
 * ## Was die ersten 36 Zeilen aus dem Betrieb (01.10., 08:28–08:53) gelehrt haben
 *
 * 1. `finger=nein` hieß „kein Finger in den letzten 150 ms" — also auch: Schwung
 *    nach dem Loslassen (iPhone) und JEDES Scrollen am Mac, wo es gar keine
 *    Touch-Ereignisse gibt (Trackpad, Mausrad, Tastatur, Klick). Fast jede
 *    Zeile trug deshalb „kein Finger" — und sagte damit nichts. Jetzt benennt
 *    `eingabe=` die Quelle: finger | nachfinger | rad | taste | klick | keine.
 * 2. Ein Wisch mit Schwung kann die Ansicht binnen 100 ms um 220+ Punkte bewegen
 *    (Physik, kein Sprung) — PLAUSIBEL, NICHT BELEGT: Die alte Zeile vom 01.10.
 *    („+236“, 08:34:43) trug kein dt; 2,4 Punkte je ms ist nur die Untergrenze
 *    bei dt = 100, bei Ereignissen im Bildtakt (16 ms) wären es 14,75 je ms.
 *    Ein Auslaufen ist stetig, ein Sprung ist ein Bruch — das Tempo allein
 *    trennt beides nicht. Darum gilt ein Schritt nur als Schwung, wenn er
 *    (a) bei Finger, Auslaufen oder Rad geschah, (b) nicht schneller ist, als
 *    ein Finger sein kann (> 10 Punkte je ms), UND (c) zur Bewegung davor
 *    passt: gleiche Richtung, höchstens das 1,5-fache des Schritts davor
 *    plus 40 Punkte (`passtZurBewegung`). Ein Programm-Sprung von 300–1000
 *    Punkten kurz nach einer Berührung (Opus-Nachprüfung: −985 bei dt = 99 mit
 *    „rad“) bleibt so eine Zeile. Ohne Eingabe gilt jeder Schritt ≥ 220.
 * 3. Wo sich die Höhe um ≥ 300 Punkte ändert, stand nirgends WER es war. Jetzt
 *    steht `ausloeser=`: die letzten seltenen Änderungen am Dokument (nur
 *    Namen von Knoten und Klassen, nie Inhalte) und der letzte Klick.
 *
 * 4. Das eigene Ort-Halten des Programms (lib/scroll-ort.ts) ist kein Sprung:
 *    Nach dem Nachladen stand `art=sprung delta=+18228 … finger=ja`, obwohl
 *    sich sichtbar nichts bewegte (am Mac, aber auch bei aufliegendem Finger:
 *    `finger=ja`). Steht die Fläche dort, wo `ortHalten` sie eben hingestellt hat
 *    — auf 150 Punkte genau, ein Finger kommt im selben Bild noch dazu —, zählt
 *    der Schritt weder als Sprung noch als Richtungswechsel (`warGehalten`); eine
 *    Zeile wegen der Höhe (mit Auslöser) trägt `gehalten=ja`.
 *
 * ## Was die Zeilen vom 02.10. (07:02–07:07, Ticket 2912) gelehrt haben
 *
 * 5. Ein Seitenwechsel in den Einstellungen (Zeile angetippt, Körper neu gebaut, `scrollTop`
 *    absichtlich 0, Höhe 1.528 → 659) stand als `art=sprung … ausloeser=-p.einst-zustand…` im
 *    Protokoll und sah aus wie der gesuchte Fehler. Im Nachbau erzeugt ein ganz normaler Tipp
 *    dieselbe Zeile. Das Programm meldet solche Wechsel jetzt (`merkeSeitenwechsel`), die Wache
 *    fragt danach (`warSeitenwechsel`) und schweigt.
 * 6. `auf=-86` heißt: unter dem Anfang. Das ist das Gummiband des iPhones (Überziehen und
 *    Zurückfedern), kein Sprung (`istGummiband`). Die Schritte dort sind groß, weil die Feder
 *    schnell zurückzieht.
 * 7. NICHT geändert: Schritte von 229–489 Punkten je Bild beim Auslaufen (14.000–28.000 Punkte/s,
 *    07:04:47, 07:07:19). Ob ein Finger das kann, ist nicht belegt — sie bleiben als Zeilen
 *    stehen. Was fehlt, ist die Bildzeit dazu: lib/scroll-takt.ts misst sie je Wisch.
 *
 * ⚠️ Höchstens 6 Zeilen je Minute, und nur bei sichtbarer Seite — eine
 *    Messung, die selbst Last erzeugt, wäre der nächste Fehler.
 * ⚠️ Gemessen wird am Scroll-Ereignis (`capture`, passiv): die Wache fasst
 *    keine Ansicht an und ändert nichts am Scrollen.
 */
import { warGehalten, warSeitenwechsel } from "./scroll-ort.js";
import { beiRuckelAenderung, gesteuert, ruckelAus } from "./ruckel-schalter.js";
import { meldeSpur } from "./mikro-spur.js";
import { eigenesSchreibenAn } from "./scroll-ruhe.js";
import { sichtLage } from "./tastatur.js";

const SPRUNG_PX = 220;
const UMKEHR_PX = 40;
const UMKEHR_FENSTER_MS = 500;
const HOEHE_PX = 40;
/** Ein Schritt gilt nur als „ein Ereignis“, wenn er so kurz nach dem letzten kam. */
const EIN_EREIGNIS_MS = 100;
export const MAX_ZEILEN_JE_MINUTE = 6;

/** Ab so viel Höhenänderung (Punkte) nennt die Zeile den Auslöser. */
const AUSLOESER_AB_PX = 300;
/** Wie lange nach dem Loslassen Schwung noch als „vom Finger“ gilt (iPhone: Auslaufen). */
export const NACHFINGER_MS = 3000;
/** Wie lange nach einem Mausrad-Dreh / Trackpad-Wisch (mit Auslaufen) der Mensch am Werk ist. */
const RAD_MS = 1500;
const TASTE_MS = 1000;
export const KLICK_MS = 800;
/** Schneller als das (Punkte je Sekunde) kann kein Finger und kein Rad die Ansicht bewegen. */
export const SCHWUNG_MAX_PX_S = 10_000;
/** Ein Schwung-Schritt darf höchstens so viel größer sein wie der Schritt davor: Faktor … */
export const SCHWUNG_FAKTOR = 1.5;
/** … plus dieser feste Spielraum (Punkte). */
export const SCHWUNG_SPIELRAUM_PX = 40;
/** So weit zurück (ms) zählen Änderungen am Dokument als möglicher Auslöser. */
export const AUSLOESER_FENSTER_MS = 900;
/** So viele Änderungen merkt sich der Ring höchstens. */
export const SPUR_MAX = 80;

/** Woher die Bewegung kam, soweit das Gerät es weiß. */
export type EingabeArt = "finger" | "nachfinger" | "rad" | "taste" | "klick" | "keine";

/** Bis wann welche Eingabe zuletzt gesehen wurde (Zeitstempel, `Date.now()`; 0 = nie). */
export interface Eingaben {
  fingerBis: number;
  letzteTouch: number;
  radBis: number;
  tasteBis: number;
  klickBis: number;
}

export function leereEingaben(): Eingaben {
  return { fingerBis: 0, letzteTouch: 0, radBis: 0, tasteBis: 0, klickBis: 0 };
}

/**
 * Welche Eingabe steht hinter diesem Scroll-Ereignis? Reine Rechnung.
 * Reihenfolge = Zuverlässigkeit: Finger auf dem Glas, Auslaufen danach, Rad,
 * Taste, Klick (löst Animationen aus), sonst keine.
 */
export function eingabeArt(jetzt: number, e: Eingaben): EingabeArt {
  if (jetzt <= e.fingerBis) return "finger";
  if (e.letzteTouch > 0 && jetzt - e.letzteTouch <= NACHFINGER_MS) return "nachfinger";
  if (jetzt <= e.radBis) return "rad";
  if (jetzt <= e.tasteBis) return "taste";
  if (jetzt <= e.klickBis) return "klick";
  return "keine";
}

/** Punkte je Sekunde; ein Schritt binnen derselben Millisekunde zählt als 1 ms. */
export function tempo(delta: number, dtMs: number): number {
  return Math.round((Math.abs(delta) / Math.max(dtMs, 1)) * 1000);
}

/**
 * Ist das ein Wisch mit Schwung — also Physik, kein Sprung? Nur bei einer
 * Eingabe von Finger, Auslaufen oder Rad, und nur bis zur Höchstgeschwindigkeit.
 */
export function istSchwung(eingabe: EingabeArt, punkteJeSekunde: number): boolean {
  return (eingabe === "finger" || eingabe === "nachfinger" || eingabe === "rad") && punkteJeSekunde <= SCHWUNG_MAX_PX_S;
}

/**
 * Passt dieser Schritt zur Bewegung davor? Ein Auslaufen ist stetig: gleiche
 * Richtung, nicht viel größer als der Schritt davor. Ein Bruch (Programm-Sprung
 * kurz nach einer Berührung) passt nicht — auch nicht, wenn es davor nichts gab.
 */
export function passtZurBewegung(altSchritt: number, delta: number): boolean {
  // Gleiche Richtung — ohne Bewegung davor (Schritt 0) hat das Vorzeichen 0 und passt zu keiner Richtung.
  if (Math.sign(altSchritt) !== Math.sign(delta)) return false;
  return Math.abs(delta) <= SCHWUNG_FAKTOR * Math.abs(altSchritt) + SCHWUNG_SPIELRAUM_PX;
}

/**
 * Steht die Fläche über dem Anfang oder unter dem Ende — oder stand sie im Bild davor so? Das ist das
 * Gummiband (iPhone): `scrollTop` wird negativ bzw. größer als das Maximum und federt zurück.
 * `sicht` = sichtbare Höhe der Fläche (0 = unbekannt: dann zählt nur „unter dem Anfang“). Eine Toleranz
 * von 1 Punkt fängt Rundung ab.
 */
export function istGummiband(oben: number, hoehe: number, sicht: number, altOben: number, altHoehe: number): boolean {
  // Ohne bekannte Sichthöhe (0: kein Layout) gibt es kein Ende, über das man ziehen könnte.
  const drueber = (o: number, h: number): boolean => o < -1 || (sicht > 0 && o > Math.max(0, h - sicht) + 1);
  return drueber(oben, hoehe) || drueber(altOben, altHoehe);
}

/** Was die Wache von einer scrollenden Fläche zuletzt gesehen hat. */
export interface ScrollStand {
  oben: number;
  hoehe: number;
  t: number;
  /** Letzter Schritt (oben − vorheriges oben) und wann die Richtung zuletzt wechselte. */
  schritt: number;
  umkehrT: number;
  umkehrAnzahl: number;
}

type ScrollBefund = {
  art: "sprung" | "umkehr" | "hoehe";
  delta: number;
  von: number;
  auf: number;
  hoehe: number;
  hoeheVorher: number;
  /** Millisekunden seit dem letzten Ereignis dieser Fläche. */
  dt: number;
  /** Das Programm selbst hat die Fläche eben dorthin gestellt (lib/scroll-ort.ts, Ort halten). */
  gehalten: boolean;
};

export function neuerStand(oben: number, hoehe: number, t: number): ScrollStand {
  return { oben, hoehe, t, schritt: 0, umkehrT: 0, umkehrAnzahl: 0 };
}

/**
 * Den neuen Stand gegen den alten halten. Reine Rechnung — kein DOM.
 * Gibt den neuen Stand und, wenn etwas auffällt, den Befund zurück.
 */
export function pruefeScroll(
  alt: ScrollStand,
  oben: number,
  hoehe: number,
  t: number,
  fingerAuf = false,
  eingabe: EingabeArt = fingerAuf ? "finger" : "keine",
  gehalten = false,
): { stand: ScrollStand; befund: ScrollBefund | null } {
  const delta = oben - alt.oben;
  const dt = t - alt.t;
  const stand: ScrollStand = { oben, hoehe, t, schritt: delta, umkehrT: alt.umkehrT, umkehrAnzahl: alt.umkehrAnzahl };
  let befund: ScrollBefund | null = null;
  const basis = { delta, von: alt.oben, auf: oben, hoehe, hoeheVorher: alt.hoehe, dt, gehalten };

  if (delta === 0) return { stand: { ...stand, schritt: alt.schritt }, befund: null };

  if (gehalten) {
    // Das Programm hat den Ort gehalten: Der Inhalt hat sich um genau diesen Schritt verschoben, der Leser nicht.
    // Weder Sprung noch Richtungswechsel — und der Schritt des Lesers davor bleibt der Maßstab für den nächsten.
    stand.schritt = alt.schritt;
    if (Math.abs(hoehe - alt.hoehe) >= HOEHE_PX && dt <= UMKEHR_FENSTER_MS) befund = { art: "hoehe", ...basis };
    return { stand, befund };
  }

  // Richtungswechsel zählen — nur große Schritte, und nur in kurzer Folge.
  const gegenlaeufig = alt.schritt !== 0 && Math.sign(delta) !== Math.sign(alt.schritt);
  if (gegenlaeufig && Math.abs(delta) >= UMKEHR_PX && Math.abs(alt.schritt) >= UMKEHR_PX && dt <= UMKEHR_FENSTER_MS) {
    stand.umkehrAnzahl = t - alt.umkehrT <= UMKEHR_FENSTER_MS ? alt.umkehrAnzahl + 1 : 1;
    stand.umkehrT = t;
  } else if (t - alt.umkehrT > UMKEHR_FENSTER_MS) {
    stand.umkehrAnzahl = 0;
  }

  // Ein Wisch mit Schwung kann die Ansicht binnen 100 ms um 220 Punkte bewegen — Physik, kein
  // Sprung. Aber nur, wenn der Schritt zur Bewegung davor passt und nicht schneller ist, als ein
  // Finger sein kann. Ein Bruch (Programm-Sprung kurz nach einer Berührung) bleibt eine Zeile.
  const schwung = dt <= EIN_EREIGNIS_MS && istSchwung(eingabe, tempo(delta, dt)) && passtZurBewegung(alt.schritt, delta);
  if (Math.abs(delta) >= SPRUNG_PX && (dt <= EIN_EREIGNIS_MS || fingerAuf) && !schwung) {
    befund = { art: "sprung", ...basis };
  } else if (stand.umkehrAnzahl >= 2) {
    befund = { art: "umkehr", ...basis };
    stand.umkehrAnzahl = 0;
  } else if (Math.abs(hoehe - alt.hoehe) >= HOEHE_PX && dt <= UMKEHR_FENSTER_MS) {
    befund = { art: "hoehe", ...basis };
  }
  return { stand, befund };
}

/** Höchstens `MAX_ZEILEN_JE_MINUTE` Zeilen in den letzten 60 s. Merkt sich jede erlaubte. */
export function drosselErlaubt(zeiten: number[], jetzt: number): boolean {
  while (zeiten.length > 0 && jetzt - zeiten[0] > 60_000) zeiten.shift();
  if (zeiten.length >= MAX_ZEILEN_JE_MINUTE) return false;
  zeiten.push(jetzt);
  return true;
}

/** Eine Änderung am Dokument: wann, und wie sie heißt (nur Namen — nie Inhalte). */
export interface Spur {
  t: number;
  s: string;
}

/** Ein Name, der nichts Persönliches tragen kann: kurz, nur Buchstaben/Ziffern/-_, keine langen Zahlenfolgen (Kennungen). */
export function sauberName(name: string | null | undefined): string {
  if (!name) return "";
  return /^[A-Za-z][A-Za-z0-9_-]{0,31}$/.test(name) && !/\d{4,}/.test(name) ? name : "";
}

/**
 * Wie ein Knoten in der Zeile heißt: `div#chat.bubble.bot[data-text]` — Tag, saubere
 * Kennung, die ersten zwei sauberen Klassen, die NAMEN der data-Attribute (nie
 * ihre Werte). Reine Rechnung auf dem, was ein Knoten hergibt.
 */
export function knotenName(
  tag: string,
  id: string | null | undefined,
  klassen: string | null | undefined,
  datenNamen: readonly string[] = [],
): string {
  const kennung = sauberName(id);
  const cls = (klassen ?? "")
    .split(/\s+/)
    .map(sauberName)
    .filter(Boolean)
    .slice(0, 2);
  const daten = datenNamen
    .filter((n) => /^data-[a-z][a-z0-9-]{0,23}$/.test(n))
    .slice(0, 2)
    .map((n) => `[${n}]`);
  return `${tag.toLowerCase()}${kennung ? "#" + kennung : ""}${cls.map((c) => "." + c).join("")}${daten.join("")}`.slice(0, 64);
}

/** Welche Klassen sind dazugekommen (+) und weggefallen (−)? Höchstens vier Namen. */
export function klassenUnterschied(alt: string | null | undefined, neu: string | null | undefined): string {
  const a = new Set((alt ?? "").split(/\s+/).filter(Boolean));
  const n = new Set((neu ?? "").split(/\s+/).filter(Boolean));
  const aus: string[] = [];
  for (const k of n) if (!a.has(k) && sauberName(k)) aus.push("+" + k);
  for (const k of a) if (!n.has(k) && sauberName(k)) aus.push("-" + k);
  return aus.slice(0, 4).join(",");
}

/** Eine Änderung in den Ring hängen; der Ring hält höchstens `SPUR_MAX`. */
export function spurAnhaengen(ring: Spur[], t: number, s: string): void {
  if (!s) return;
  ring.push({ t, s: s.slice(0, 96) });
  if (ring.length > SPUR_MAX) ring.splice(0, ring.length - SPUR_MAX);
}

/**
 * Was kommt in die Zeile? Aus den Änderungen der letzten `fensterMs` die
 * SELTENEN zuerst: Beim Streamen ändert sich dieselbe Blase bei jedem Stück —
 * das ist Rauschen. Was einmal geschah (eine Klasse kommt dazu, eine Gruppe
 * geht auf, ein Behälter verschwindet), ist der Verdächtige. Gleiche Namen
 * werden gezählt (`name×3`). Höchstens `max` Namen, gesamt höchstens 220 Zeichen.
 */
export function ausloeserText(ring: readonly Spur[], jetzt: number, fensterMs = AUSLOESER_FENSTER_MS, max = 5): string {
  const zaehler = new Map<string, { n: number; zuletzt: number }>();
  for (const e of ring) {
    const alter = jetzt - e.t;
    // Zu alt zählt nicht — und was in der Zukunft liegt (Uhr verstellt) auch nicht.
    if (alter > fensterMs || alter < 0) continue;
    const z = zaehler.get(e.s);
    if (z) {
      z.n++;
      z.zuletzt = Math.max(z.zuletzt, e.t);
    } else zaehler.set(e.s, { n: 1, zuletzt: e.t });
  }
  const teile = [...zaehler.entries()]
    .sort((x, y) => x[1].n - y[1].n || y[1].zuletzt - x[1].zuletzt)
    .slice(0, max)
    .map(([name, z]) => (z.n > 1 ? `${name}×${z.n}` : name));
  return teile.join(";").slice(0, 220);
}

const zeiten: number[] = [];

function erlaubt(): boolean {
  return document.visibilityState === "visible" && drosselErlaubt(zeiten, Date.now());
}

/** Wie sich die Fläche nennt: id, sonst die erste Klasse. */
export function nameVon(el: Element): string {
  if (el.id) return el.id;
  const k = el.classList?.[0];
  return k ? `.${k}` : el.tagName.toLowerCase();
}

/**
 * Die sichtbare Lage des Fensters — das, was bei der Tastatur-Rechnung zählt. Seit 07.10.2026 mit der
 * Höhe des Layouts (`layout=`, die Wurzel) und der verdeckten Höhe, wie lib/tastatur.ts sie rechnet
 * (`verdeckt=`). Vorher stand hier `fehlt=` = innerHeight − vv − vvTop: auf Martins iPhone −401 bei offener
 * Tastatur, weil dort innerHeight der sichtbaren Höhe folgt.
 */
function fensterLage(): string {
  const vv = window.visualViewport;
  const wurzel = document.documentElement.clientHeight;
  const lage = vv ? sichtLage(window.innerHeight, wurzel, vv.height, vv.offsetTop, vv.scale) : null;
  return (
    `inner=${Math.round(window.innerHeight)} vv=${vv ? Math.round(vv.height) : "-"} vvTop=${vv ? Math.round(vv.offsetTop) : "-"} ` +
    `layout=${Math.round(wurzel)} verdeckt=${lage ? Math.round(lage.verdeckt) : 0}`
  );
}

/**
 * Die Rechnung sagt „Tastatur“ (es fehlen > 80 Punkte), aber kein Feld hat den
 * Fokus — früher hätte das `body.keyboard-open` gesetzt und die Fußzeile
 * weggenommen (tastatur.ts). Einmal je Fall, mit den Zahlen dazu (`verdeckt=` steht in der Lage).
 */
export function meldeTastaturOhneFeld(fokus: string): void {
  if (ruckelAus("wachen") || !erlaubt()) return;
  meldeSpur("scroll-t", `art=tastatur-ohne-feld fokus=${fokus} ${fensterLage()}`);
}

/** Tasten, mit denen man scrollt (Leertaste, Bild auf/ab, Pfeile, Pos1/Ende). */
const SCROLL_TASTEN = new Set([" ", "PageUp", "PageDown", "Home", "End", "ArrowUp", "ArrowDown"]);

function datenNamen(el: Element): string[] {
  try {
    return el.getAttributeNames().filter((n) => n.startsWith("data-"));
  } catch {
    return [];
  }
}

/** Ein Element mit seinem Namen für die Zeile — Tag, Kennung, Klassen, data-Namen. Nie Inhalt. */
function elementName(el: Element): string {
  return knotenName(el.tagName, el.id, el.getAttribute("class"), datenNamen(el));
}

function knotenListe(liste: NodeList): string {
  const namen: string[] = [];
  for (let i = 0; i < Math.min(2, liste.length); i++) {
    const n = liste[i];
    namen.push(n?.nodeType === 1 ? elementName(n as Element) : "#text");
  }
  return namen.join(",") + (liste.length > 2 ? `…${liste.length}` : "");
}

/**
 * Was ändert sich am Dokument? Jede Änderung (hinzu, weg, Klasse, hidden, open)
 * kommt als Name in den Ring — dort liest die Zeile nach, wenn die Höhe um ≥ 300
 * Punkte springt. Die Rechnung je Änderung ist winzig; bei Massenänderungen
 * (Verlauf neu aufbauen) werden nur die ersten und letzten 30 angesehen.
 */
function beobachteAenderungen(ring: Spur[]): void {
  if (typeof MutationObserver === "undefined") return;
  const wurzel = document.getElementById("app") ?? document.body;
  if (!wurzel) return;
  const beobachter = new MutationObserver((aenderungen) => {
    if (document.visibilityState !== "visible") return;
    const t = Date.now();
    const n = aenderungen.length;
    const auswahl = n <= 60 ? aenderungen : [...aenderungen.slice(0, 30), ...aenderungen.slice(n - 30)];
    for (const a of auswahl) {
      try {
        if (a.type === "childList" && a.target instanceof Element) {
          const wo = elementName(a.target);
          if (a.removedNodes.length) spurAnhaengen(ring, t, `-${knotenListe(a.removedNodes)}@${wo}`);
          if (a.addedNodes.length) spurAnhaengen(ring, t, `+${knotenListe(a.addedNodes)}@${wo}`);
        } else if (a.type === "attributes" && a.target instanceof Element) {
          const name = a.attributeName ?? "";
          const jetzt = a.target.getAttribute(name);
          const was = name === "class" ? klassenUnterschied(a.oldValue, jetzt) : jetzt === null ? "aus" : "an";
          spurAnhaengen(ring, t, `@${name}${was ? `(${was})` : ""}@${elementName(a.target)}`);
        }
      } catch {
        /* Messen darf nie stören. */
      }
    }
  });
  const optionen: MutationObserverInit = {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["class", "hidden", "open"],
    attributeOldValue: true,
  };
  // Ruckel-Test (Ticket 2912): bei „Scroll-Messfühler aus“ gar nicht beobachten, nicht nur schweigen.
  if (!ruckelAus("wachen")) beobachter.observe(wurzel, optionen);
  beiRuckelAenderung((id, jetztAn) => {
    if (id !== "wachen") return;
    if (jetztAn) beobachter.disconnect();
    else beobachter.observe(wurzel, optionen);
  });
}

/** Was das Gerät zuletzt an Eingaben gesehen hat — auch für lib/scroll-takt.ts. */
const eingaben = leereEingaben();
/*
 * Ob der Leser gerade scrollt, entscheidet seit pre.3607 lib/scroll-ruhe.ts (`nutzerScrolltGerade`) —
 * EINE Regel für alle Schreiber, unabhängig vom Ruckel-Schalter. Hier stand bis dahin eine zweite
 * (`nutzerBewegtSich`: 400 ms nach jeder Berührung oder jedem Scroll-Ereignis, auch dem eigenen), die nur
 * die Einstellungen fragten, während der Chat eine dritte eigene Frist hatte.
 */

/** Die Eingabe hinter dem jetzigen Moment (Finger, Auslaufen, Rad, Taste, Klick, keine). */
export function leseEingabe(jetzt = Date.now()): EingabeArt {
  return eingabeArt(jetzt, eingaben);
}

let laeuft = false;

/** Die Wache einschalten (einmal). */
export function starteScrollWache(): void {
  if (laeuft || typeof document === "undefined") return;
  laeuft = true;
  const staende = new WeakMap<Element, ScrollStand>();
  const ring: Spur[] = [];
  let klickName = "";
  // Alle Eingabe-Wachen hängen am Schalter „Scroll-Messfühler aus“ (Ticket 2912). Sie messen nur;
  // ob jemand scrollt, entscheidet lib/scroll-ruhe.ts (hängt NICHT am Schalter).
  const beiTouch = gesteuert("wachen", (): void => {
    const t = Date.now();
    eingaben.fingerBis = t + 150;
    eingaben.letzteTouch = t;
  });
  for (const art of ["touchstart", "touchmove", "touchend", "touchcancel"]) {
    document.addEventListener(art, beiTouch, { capture: true, passive: true });
  }
  // Am Mac gibt es keine Touch-Ereignisse: Rad und Trackpad kommen als `wheel`
  // (mit Auslaufen), Tasten und Klicks als sie selbst.
  document.addEventListener(
    "wheel",
    gesteuert("wachen", () => {
      eingaben.radBis = Date.now() + RAD_MS;
    }),
    { capture: true, passive: true },
  );
  document.addEventListener(
    "keydown",
    gesteuert("wachen", (ev: KeyboardEvent) => {
      if (SCROLL_TASTEN.has(ev.key)) eingaben.tasteBis = Date.now() + TASTE_MS;
    }),
    { capture: true, passive: true },
  );
  document.addEventListener(
    "click",
    gesteuert("wachen", (ev: MouseEvent) => {
      eingaben.klickBis = Date.now() + KLICK_MS;
      try {
        const z = ev.target instanceof Element ? (ev.target.closest("button, a, summary, [role=button], label") ?? ev.target) : null;
        klickName = z ? elementName(z) : "";
      } catch {
        klickName = "";
      }
    }),
    { capture: true, passive: true },
  );
  beobachteAenderungen(ring);

  document.addEventListener(
    "scroll",
    gesteuert("wachen", (ev: Event) => {
      try {
        const ziel = ev.target;
        const el = ziel instanceof Element ? ziel : document.scrollingElement;
        if (!el) return;
        const t = Date.now();
        const alt = staende.get(el);
        if (!alt) {
          staende.set(el, neuerStand(el.scrollTop, el.scrollHeight, t));
          return;
        }
        // Ein Seitenwechsel (neuer Inhalt, Anfang) und das Gummiband sind keine Sprünge — neue Grundlinie.
        if (warSeitenwechsel(el, t) || istGummiband(el.scrollTop, el.scrollHeight, el.clientHeight, alt.oben, alt.hoehe)) {
          staende.set(el, neuerStand(el.scrollTop, el.scrollHeight, t));
          return;
        }
        const eingabe = eingabeArt(t, eingaben);
        const gehalten = warGehalten(el, el.scrollTop, t);
        const r = pruefeScroll(alt, el.scrollTop, el.scrollHeight, t, t <= eingaben.fingerBis, eingabe, gehalten);
        staende.set(el, r.stand);
        if (!r.befund || !erlaubt()) return;
        const b = r.befund;
        const dock = document.querySelector(".dock");
        const dockSichtbar = !!dock && getComputedStyle(dock).display !== "none";
        const hoeheSprung = Math.abs(b.hoehe - b.hoeheVorher) >= AUSLOESER_AB_PX;
        meldeSpur(
          "scroll-t",
          `art=${b.art} ziel=${nameVon(el)} delta=${b.delta > 0 ? "+" : ""}${Math.round(b.delta)} ` +
            `von=${Math.round(b.von)} auf=${Math.round(b.auf)} hoehe=${b.hoeheVorher}>${b.hoehe} ` +
            `finger=${t <= eingaben.fingerBis ? "ja" : "nein"} eingabe=${eingabe} dt=${Math.round(b.dt)} v=${tempo(b.delta, b.dt)} ` +
            `kb=${document.body.classList.contains("keyboard-open") ? "ja" : "nein"} ` +
            `dock=${dockSichtbar ? "ja" : "nein"} ${fensterLage()} seite=${Math.round(document.scrollingElement?.scrollTop ?? 0)}` +
            (b.gehalten ? " gehalten=ja" : "") +
            // pre.3607: kam der Schritt aus dem Code (lib/scroll-ruhe.ts, schreibeScrollTop)? `-` = Leser oder Browser.
            ` schreiber=${eigenesSchreibenAn(el) ?? "-"}` +
            (hoeheSprung ? ` ausloeser=${ausloeserText(ring, t) || "-"}` : "") +
            (t <= eingaben.klickBis && klickName ? ` klick=${klickName}` : ""),
        );
      } catch {
        /* Messen darf nie stören. */
      }
    }),
    { capture: true, passive: true },
  );
}
